import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, AlertTriangle, ShieldCheck, ArrowRight, Clock } from "lucide-react";
import { ROI_BASE, readRoiDelta } from "@/lib/roi";

const fmtK = (v) => `€${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}K`;

const Card = ({ title, sub, children, testId }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid={testId}>
    <h3 className="text-sm font-semibold text-zinc-200">{title}</h3>
    {sub && <p className="text-[11px] text-zinc-500 mt-0.5">{sub}</p>}
    <div className="mt-3">{children}</div>
  </div>
);

export default function ExecutiveView() {
  const [d, setD] = useState(readRoiDelta);
  useEffect(() => {
    const h = () => setD(readRoiDelta());
    window.addEventListener("onet-roi-delta", h);
    return () => window.removeEventListener("onet-roi-delta", h);
  }, []);

  const B = ROI_BASE;
  const pulse = [
    { label: "Value currently identified across at-risk lifecycle opportunities", value: fmtK(B.valueAtRiskTotal) },
    { label: "Assisted FTD value", value: fmtK(B.summary.assistedFtdValue + d.assistedFtdValue), changed: d.assistedFtdValue > 0 },
    { label: "Assisted client progressions", value: B.summary.assistedProgressions + d.assistedFtdCount + d.reengaged, changed: d.assistedFtdCount > 0 || d.reengaged > 0 },
    { label: "Cases requiring human intervention", value: B.summary.human + d.escalations, changed: d.escalations > 0 },
    { label: "Actions prevented by governance", value: B.summary.governancePrevented + d.governance, changed: d.governance > 0 },
    { label: "Average eligible intervention time", value: `${B.summary.avgInterventionSec} sec` },
  ];

  const attention = [
    { label: `${B.humanImpact.open + d.escalations} High-Value Cases Awaiting Human Action`, sub: `Combined account value ${fmtK(B.humanImpact.value)}`, to: "/views/operations", testId: "attention-human" },
    { label: "3 SLA-Tracked Human Tasks", sub: "Payments · Compliance · Retention", to: "/views/operations", testId: "attention-sla" },
    { label: `${fmtK(B.openOpportunity.reduce((a, b) => a + b.value, 0))} Open Opportunity Value`, sub: "Still unresolved", to: "/impact", testId: "attention-open" },
    { label: `${B.governance.prevented + d.governance} Governance Blocks`, sub: "No automated communication permitted", to: "/views/compliance", testId: "attention-governance" },
  ];

  return (
    <div className="space-y-6" data-testid="executive-view">
      <div>
        <div className="text-[10px] font-mono tracking-widest text-emerald-500 uppercase mb-1">Executive View · Simulated demo data</div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100">Brokerage Operational Pulse</h1>
        <p className="text-sm text-zinc-400 mt-1">Where value is leaking, what O.N.E.Tech is doing about it, and what outcomes are being produced.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="exec-pulse">
        {pulse.map((p, i) => (
          <div key={i} className={`bg-zinc-900 border rounded-md p-4 ${p.changed ? "border-emerald-500/50 shadow-[0_0_15px_rgba(16,185,129,0.12)]" : "border-zinc-800"}`}>
            <div className={`text-2xl font-bold font-mono tracking-tight ${p.changed ? "text-emerald-400" : "text-zinc-100"}`}>{p.value}</div>
            <div className="text-[10px] text-zinc-400 uppercase tracking-wider mt-1.5 leading-snug">{p.label}</div>
          </div>
        ))}
      </div>

      <Card title="Requires Attention" sub="Click through to the working view" testId="exec-attention">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {attention.map((a) => (
            <Link key={a.testId} to={a.to} data-testid={a.testId}
              className="border border-zinc-800 bg-zinc-950/50 hover:border-emerald-500/40 rounded-sm p-4 transition-colors duration-200 group">
              <div className="flex items-start justify-between gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" strokeWidth={1.5} />
                <ArrowRight className="w-3.5 h-3.5 text-zinc-600 group-hover:text-emerald-400 transition-colors duration-200" />
              </div>
              <div className="text-xs font-semibold text-zinc-100 mt-2 leading-snug">{a.label}</div>
              <div className="text-[10px] text-zinc-500 mt-1">{a.sub}</div>
            </Link>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Outcome Attribution Split" sub="Every outcome belongs to exactly one category" testId="exec-attribution">
          <div className="space-y-2.5">
            {B.attribution.map((a) => {
              const v = a.value + (a.deltaKey ? d[a.deltaKey] || 0 : 0);
              const total = B.attribution.reduce((x, y) => x + y.value, 0) + d.assistedFtdValue + d.organicValue;
              return (
                <div key={a.key} className="flex items-center gap-3">
                  <span className="text-xs text-zinc-400 w-40">{a.label}</span>
                  <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${a.tone === "emerald" ? "bg-emerald-500" : a.tone === "sky" ? "bg-sky-400" : a.tone === "amber" ? "bg-amber-400" : "bg-zinc-500"}`}
                      style={{ width: `${(v / total) * 100}%` }} />
                  </div>
                  <span className="font-mono text-xs text-zinc-200 w-20 text-right">{fmtK(v)}</span>
                </div>
              );
            })}
          </div>
        </Card>

        <Card title="Lifecycle Leakage" sub="Potential value at risk — not revenue lost" testId="exec-leakage">
          <div className="space-y-2">
            {B.leakageAreas.map((a) => (
              <div key={a.area} className="flex items-center justify-between text-xs border-b border-zinc-800/60 pb-1.5">
                <span className="text-zinc-400">{a.area} <span className="text-zinc-600">({a.opportunities})</span></span>
                <span className="font-mono text-red-400">{fmtK(a.value)}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            <Link to="/impact" data-testid="exec-open-impact" className="flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-zinc-950 transition-colors duration-200">
              Full Operational Impact <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link to="/live" data-testid="exec-open-live" className="flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-semibold bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 transition-colors duration-200">
              Live Operations
            </Link>
          </div>
        </Card>
      </div>

      <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-600 tracking-widest uppercase" data-testid="exec-model-note">
        <Clock className="w-3 h-3 text-emerald-500" strokeWidth={1.5} />
        Detect → Understand → Govern → Act → Measure
      </div>
    </div>
  );
}
